import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings2, Boxes } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function MasterAuthorisation() {
  const nav = useNavigate();
  const [ests, setEsts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/admin/establishments"), api.get("/organisation")])
      .then(([e, o]) => { setEsts(e.data); setOrgs(o.data); })
      .catch(err => toast.error(formatErr(err)));
  }, []);

  const filtered = useMemo(() => ests.filter(b => {
    if (orgFilter !== "all" && b.org_id !== orgFilter) return false;
    if (statusFilter === "active" && !b.is_active) return false;
    if (statusFilter === "inactive" && b.is_active) return false;
    return true;
  }), [ests, orgFilter, statusFilter]);
  const pg = usePagination(filtered, statusFilter);

  return (
    <div className="p-6 space-y-6" data-testid="mauth-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Boxes className="w-6 h-6 text-primary"/> Master Authorisation</h1>
          <p className="text-muted-foreground">Authorise shared master data access per establishment.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Organisation</Label>
              <Select value={orgFilter} onValueChange={setOrgFilter}>
                <SelectTrigger data-testid="mauth-filter-org" className="mt-1 w-64"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="mauth-org-all" value="all">All Organisations</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} data-testid={`mauth-org-${o.id}`} value={o.id}>{o.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="mauth-filter-status" className="mt-1 w-40"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="mauth-status-all" value="all">All Status</SelectItem>
                  <SelectItem data-testid="mauth-status-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="mauth-status-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="ml-auto" data-testid="mauth-count">{filtered.length} of {ests.length} shown</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="mauth-table">
            <TableHeader>
              <TableRow>
                <TableHead>Establishment Name</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map(b => (
                <TableRow key={b.id} data-testid={`mauth-row-${b.id}`}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="text-sm">{b.org_name}</TableCell>
                  <TableCell>
                    <Badge variant={b.is_active ? "default" : "destructive"} data-testid={`mauth-status-badge-${b.id}`}>
                      {b.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => nav(`/admin/master-auth/${b.id}`)}
                      data-testid={`mauth-manage-${b.id}`}>
                      <Settings2 className="w-3.5 h-3.5 mr-1"/> Manage Masters
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8" data-testid="mauth-empty">
                  {ests.length ? "No establishments match the current filters." : "No establishments yet"}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <PaginationBar pg={pg}/>
        </CardContent>
      </Card>
    </div>
  );
}
